import type { ScanOutcome, StageCaptureResult, StagedCapture } from '../shared/desktop-api';

/** Uploads files into the job's workspace through the session and records the staged capture (browser-captures). */
export type StageFiles = (jobId: string, files: File[], label: string) => Promise<StagedCapture>;

/**
 * The scan the screen asked for: CapturePoint's `stageScan` is awaited until the scan sheet either
 * adds pages (the staged capture, which the screen queues and drains) or is closed (canceled,
 * which the screen reads as the operator's own decision and does not report).
 */
export interface ScanSession {
  jobId: string;
  addPages: (files: File[], label: string) => Promise<void>;
  cancel: () => void;
  /** Whether Add to job is uploading this scan's pages right now. */
  adding: () => boolean;
  /** Stop, pressed on the screen behind the sheet: the scan ends as stopped, with nothing added. */
  stop: () => void;
}

let current: ScanSession | null = null;
/** The open scan sheet's ways of hearing Stop: Paige Scan is told to stop feeding, then the sheet closes. */
const whenStopped = new Set<() => void>();

export function openScanSession(jobId: string, stage: StageFiles): Promise<StageCaptureResult> {
  current?.cancel();
  return new Promise<StageCaptureResult>((resolve) => {
    // Pages on their way into the job: Stop must not call that scan stopped with nothing added.
    let adding = false;
    const session: ScanSession = {
      jobId,
      addPages: async (files, label) => {
        adding = true;
        try {
          const capture = await stage(jobId, files, label);
          if (current === session) current = null;
          resolve({ canceled: false, capture });
        } finally {
          adding = false;
        }
      },
      cancel: () => {
        if (current === session) current = null;
        resolve({ canceled: true });
      },
      adding: () => adding,
      stop: () => {
        if (current === session) current = null;
        resolve({ canceled: false, scan: stoppedWithNothing() });
      },
    };
    current = session;
  });
}

export function currentScanSession(): ScanSession | null {
  return current;
}

/** CapturePoint's account of a scan stopped before any page reached the job. */
function stoppedWithNothing(): ScanOutcome {
  return {
    kind: 'stopped',
    captureId: '',
    keptPages: 0,
    excludedPages: 0,
    reason: 'stopped',
    message: null,
    scannerReleased: true,
  };
}

/** What the open scan sheet does when the screen's Stop ends its scan; heard in the order asked. */
export function onScanStopped(listener: () => void): () => void {
  whenStopped.add(listener);
  return () => {
    whenStopped.delete(listener);
  };
}

/**
 * CapturePoint's Stop (`cancelScan`), which stays on the screen behind Paige Scan's sheet: Paige
 * Scan is told to stop a scan it is feeding (the sheet's own Stop), the scan the screen is waiting
 * on ends as stopped, and the sheet closes. True when there was a scan to stop; false tells the
 * screen there was none, in its own words. The scan settles a moment after the answer, as the
 * desktop's does, so the screen's "settling" mark is set before the end clears it. While Add to job
 * is uploading the scan's pages there is nothing left to stop: the answer is false, the sheet stays
 * and the pages arrive as the capture they are. (The screen behind an open sheet is inert,
 * IntakeSheet.tsx, so this is the answer for whatever reaches Stop anyway.)
 */
export function stopScanSession(): boolean {
  const session = current;
  if (!session || session.adding()) return false;
  setTimeout(() => {
    session.stop();
    for (const listener of [...whenStopped]) listener();
  }, 0);
  return true;
}
